import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaChevronRight, FaShieldAlt } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../../api/axios";
import { BenefitBadge, BenefitInfo } from "../../../components/card/BenefitBadgeCard";
import HeaderWithBack from "../../../components/HeaderWithBack";
import Loading from "../../../components/Loading";
import { useCheckout } from "../../../hooks/useCheckout";
import { usePayment } from "../../../hooks/usePayment";
import { useAuthStore } from "../../../stores/authStores";
import { useCheckoutStore } from "../../../stores/checkoutStore";
import { formatRupiah } from "../../../utils/formatPrice";
import FullscreenLoading from "../../Prescription/Components/FullScreenLoader";

interface CheckoutInfo {
  doctor: {
    name: string;
    specialization: string;
    photoUrl: string;
    price_chat: number;
    price_video: number;
  };
  transaction_fee: number;
  benefit?: BenefitInfo;
}

const CheckoutTeleconsultation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { practitionerRoleId } = useParams<{ practitionerRoleId: string }>();
  const userData = useAuthStore((state) => state.user);
  const authToken = useAuthStore((state) => state.token);

  const selectedPayment = useCheckoutStore((s) => s.selectedPayment);
  const selectedVoucher = useCheckoutStore((s) => s.selectedVoucher);
  const setPayment = useCheckoutStore((s) => s.setPayment);
  const setVoucher = useCheckoutStore((s) => s.setVoucher);
  const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);

  const isAuto = practitionerRoleId === 'auto';
  const { data: checkoutInfo, isLoading } = useCheckout({
    authToken,
    practitionerRoleId,
    isAuto,
    selectedVoucher,
    selectedPayment,
  });
  const { paying, createPayment } = usePayment({
    authToken,
    navigate,
  });

  const showToast = (message: string, type: "error" | "success" = "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const serviceType = location.state?.serviceType || "chat";
  const getPertanyaan = (): any => {
    if (location.state?.pertanyaan) return location.state.pertanyaan;
    const saved = localStorage.getItem("teleconsult_disclaimer");
    if (saved) { try { return JSON.parse(saved); } catch {} }
    return { berat: "", tinggi: "", lamaSakit: "", minumObat: "", riwayatCovid: "" };
  };
  const pertanyaan = getPertanyaan();

  useEffect(() => {
    if (location.state?.selectedVoucher) {
      setVoucher(location.state.selectedVoucher);
    }
    if (location.state?.selectedPayment) {
      setPayment(location.state.selectedPayment);
    }
  }, []);

  if (isLoading || !checkoutInfo) {
    return <FullscreenLoading />;
  }

  const consultationPrice =
    serviceType === "video"
      ? checkoutInfo.doctor.price_video
      : checkoutInfo.doctor.price_chat;

  const transactionFee  = checkoutInfo.transaction_fee;
  const subtotal = consultationPrice + transactionFee;
  const voucherDiscount = checkoutInfo?.discount ?? 0;
  const benefitInfo     = checkoutInfo.benefit;
  const totalAfterDiscount = checkoutInfo.total;
  const isFree = totalAfterDiscount === 0;
  const handleSubmitPayment = async () => {
    if (!isFree && !selectedPayment) { 
      showToast("Pilih metode pembayaran dulu"); 
      return; 
    }
    if (!pertanyaan.berat || !pertanyaan.tinggi) {
      showToast("Data berat dan tinggi badan belum lengkap.");
      return;
    }

    try {
      const payload: any = {
        user_id: userData.id,
        practitioner_role_id: isAuto ? null : practitionerRoleId,
        doctor_assign_status: isAuto ? "unassigned" : "assigned",
        pertanyaan,
        voucher_id: selectedVoucher?.id || null,
        total_amount: totalAfterDiscount,
      };

      if (!isFree) {
        payload.payment_method_id = selectedPayment?.id;
      }
      await createPayment({
        payload,
        checkoutInfo,
        onVoucherChanged: () => {
          showToast("Voucher berubah");
        },
      });
    } catch (err: any) {
      showToast(err.response?.data?.message || "Payment gagal");
    }
  };
  

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col pb-32">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999]">
          <div
            className={`flex items-start gap-3 px-5 py-4 rounded-xl shadow-lg text-white text-sm
              max-w-sm w-[90vw] animate-slideDown
              ${toast.type === "error" ? "bg-red-500" : "bg-teal-500"}`}
          >
            <span className="text-lg">{toast.type === "error" ? "❌" : "✅"}</span>
            <div className="flex-1">
              <p className="font-semibold">{toast.type === "error" ? "Gagal" : "Berhasil"}</p>
              <p className="opacity-90">{toast.message}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-white opacity-70 hover:opacity-100 text-lg">×</button>
          </div>
        </div>
      )}

      <HeaderWithBack title="Pembayaran" url={`/disclaimer/${practitionerRoleId}`} />

      {/* Doctor Card */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
          <img src={checkoutInfo.doctor.photoUrl} alt="Doctor" className="w-12 h-12 rounded-full object-cover" />
          <div className="flex-1">
            <p className="font-semibold text-gray-800">{checkoutInfo.doctor.name}</p>
            <p className="text-sm text-gray-500">{checkoutInfo.doctor.specialization}</p>
            {isAuto && <p className="text-xs text-teal-600 mt-1">✨ Dokter akan dipilihkan otomatis</p>}
          </div>
        </div>

        <div className="mt-3 bg-pink-100 text-pink-700 rounded-lg px-3 py-2 flex items-center gap-2 text-sm">
          <FaCheckCircle />
          <span>Kami menjamin privasi anda saat berkonsultasi</span>
        </div>

        {/* ── BENEFIT BADGE ── */}
        {benefitInfo && (
          <BenefitBadge benefit={benefitInfo} consultationPrice={consultationPrice} />
        )}
      </div>

      {/* Rincian Pesanan */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="font-semibold text-gray-800 mb-3">Rincian Pesanan</p>

          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Konsultasi ({serviceType})</span>
            <span>{formatRupiah(consultationPrice)}</span>
          </div>

          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Biaya Transaksi</span>
            <span>{formatRupiah(transactionFee)}</span>
          </div>

          {selectedVoucher && (
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Voucher</span>
              <span className="text-teal-600">- {formatRupiah(voucherDiscount)}</span>
            </div>
          )}

          {/* Benefit row */}
          {benefitInfo?.eligible && (
            <div className="flex justify-between text-sm mb-2 items-center">
              <span className="flex items-center gap-1.5 text-gray-600">
                <FaShieldAlt className="text-emerald-500 text-xs" />
                Benefit Asuransi
              </span>
              <span className="text-emerald-600 font-medium">
                Ditanggung Asuransi
              </span>
            </div>
          )}

          <hr className="my-2" />

          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <div className="text-right">
              {(voucherDiscount > 0 || benefitInfo?.eligible) && (
                <p className="text-xs text-gray-400 line-through">{formatRupiah(subtotal)}</p>
              )}
              <span className="text-teal-600">{formatRupiah(totalAfterDiscount)}</span>
            </div>
          </div>

          {/* Hemat banner */}
          {(voucherDiscount > 0 || benefitInfo?.eligible) && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <span className="text-emerald-600 text-lg">🎉</span>
              <span className="text-emerald-700 text-xs font-medium">
                Kamu hemat{" "}
                {formatRupiah(
                  benefitInfo?.eligible ? subtotal : voucherDiscount
                )}{" "}
                dari total tagihan!
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Payment Method */}
      {isFree ?? 
        <>
          <div className="px-4 mt-4">
            <p className="text-md font-bold mb-2">Metode Pembayaran</p>
            <div
              onClick={() =>
                navigate(`/payment-method/${practitionerRoleId}`, {
                  state: { isAuto, selectedPayment, selectedVoucher, subtotal, pertanyaan },
                })
              }
              className="bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <img
                  src={selectedPayment?.displayIcon || "/app-icons/payment.svg"}
                  alt="payment"
                  className="w-8 h-8 object-contain"
                />
                <p className="font-medium">
                  {selectedPayment ? selectedPayment.displayName : "Pilih Metode Pembayaran"}
                </p>
              </div>
              <FaChevronRight />
            </div>
          </div>

          {/* Voucher */}
          <div className="px-4 mt-4">
            <p className="text-md font-bold mb-2">Voucher</p>
            <div
              onClick={() =>
                navigate(`/voucher/${practitionerRoleId}`, {
                  state: { isAuto, selectedPayment, selectedVoucher, pertanyaan, subtotal },
                })
              }
              className="bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <img src="/app-icons/voucher.svg" className="w-8 h-8 object-contain" alt="voucher" />
                <div>
                  <p className="font-medium">{selectedVoucher ? selectedVoucher.code : "Gunakan Voucher"}</p>
                  {selectedVoucher && (
                    <p className="text-sm text-gray-500">{formatRupiah(voucherDiscount)}</p>
                  )}
                </div>
              </div>
              <FaChevronRight />
            </div>
          </div>
        </>
      }

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow px-4 py-3 flex justify-between items-center">
        <div>
          <p className="text-xs text-gray-500">Total Bayar</p>
          <p className="text-teal-600 font-bold text-lg">{formatRupiah(totalAfterDiscount)}</p>
          {benefitInfo?.eligible && (
            <div className="text-xs text-teal-600 mt-2">
              Asuransi akan diproses setelah pembayaran berhasil
            </div>
          )}
        </div>
        <button
          onClick={handleSubmitPayment}
          disabled={paying}
          className="bg-teal-500 text-white px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2"
        >
          {isFree ? "Mulai Konsultasi" : "Bayar Sekarang"}
        </button>
      </div>
    </div>
  );
};

export default CheckoutTeleconsultation;
